import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { Toaster } from 'sonner';
import App from './App';
import { theme } from './theme';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { APP_NAME } from './config/app';
import './i18n';
import './index.css';

document.title = APP_NAME;

// 记录第一次进入应用的 Entry URL 用于 iOS / Mac WKWebView 的 WeCom 签名缓存 Bug
if (!(window as any).__wecom_entry_url) {
  (window as any).__wecom_entry_url = window.location.href.split('#')[0];
}

// 创建 Query Client 实例
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000, // 5分钟
      gcTime: 30 * 60 * 1000, // 30分钟
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

const getErrorMessage = (error: unknown): string => {
  if (!error) return '';
  if (error instanceof Error) return error.message || error.name || '';
  if (typeof error === 'string') return error;
  if (typeof error === 'object') {
    const record = error as Record<string, unknown>;
    const message = record.message || record.detail || record.error || record.errmsg;
    if (typeof message === 'string') return message;
    try {
      return JSON.stringify(error);
    } catch {
      return '页面运行时发生异常，请刷新后重试。';
    }
  }
  return String(error);
};

const isFatalLoadError = (error: unknown) => {
  const message = getErrorMessage(error);
  const name = error instanceof Error ? error.name : '';
  return /ChunkLoadError|Loading chunk|dynamically imported module|Importing a module script failed|Failed to fetch dynamically imported module/i
    .test(`${name} ${message}`);
};

const dispatchFatalError = (error: unknown) => {
  window.dispatchEvent(new CustomEvent('hhcd:fatal-error', { detail: getErrorMessage(error) }));
};

// 全局错误处理：生产环境也展示可见降级页，避免整页空白。
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  if (isFatalLoadError(event.reason)) {
    dispatchFatalError(event.reason);
  }
});

window.addEventListener('error', (event) => {
  console.error('Global error:', event.error || event.message);
  const error = event.error || event.message;
  if (isFatalLoadError(error)) {
    dispatchFatalError(error);
  }
});

const RootErrorFallback: React.FC<{ message?: string }> = ({ message }) => (
  <div style={{ padding: 32, fontFamily: 'system-ui, sans-serif', color: '#1f2937' }}>
    <h2 style={{ margin: '0 0 12px', fontSize: 20 }}>页面加载异常</h2>
    <p style={{ margin: '0 0 20px', color: '#6b7280' }}>
      {message || '页面运行时发生异常，请刷新后重试。'}
    </p>
    <button
      type="button"
      onClick={() => window.location.reload()}
      style={{
        border: 0,
        borderRadius: 6,
        padding: '8px 16px',
        background: '#2196f3',
        color: '#fff',
        cursor: 'pointer',
      }}
    >
      刷新页面
    </button>
  </div>
);

const RootShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [fatalError, setFatalError] = React.useState<unknown>(null);

  React.useEffect(() => {
    const onFatalError = (event: Event) => {
      setFatalError((event as CustomEvent).detail || '未知错误');
    };
    window.addEventListener('hhcd:fatal-error', onFatalError);
    return () => window.removeEventListener('hhcd:fatal-error', onFatalError);
  }, []);

  if (fatalError) {
    const message = getErrorMessage(fatalError);
    return <RootErrorFallback message={message} />;
  }

  return <>{children}</>;
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <RootShell>
            <ErrorBoundary fallback={<RootErrorFallback />}>
              <App />
            </ErrorBoundary>
          </RootShell>
          <Toaster
            position="top-right"
            richColors
            closeButton
            expand={false}
            duration={4000}
          />
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>
);
